// Import React
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';

import styled from 'styled-components';
import { readAndCompressImage } from 'browser-image-resizer';
import { ckeditorConfig, shortCkeditorConfig } from '../services/ckeditorConfig';

interface TextEditorProps {
  content: string;
  setContent?: Dispatch<SetStateAction<string>>;
  readOnly: boolean;
  disabled?: boolean;
  idx?: number; // 슬라이드에서 인덱스 넘겨줄때 사용
  toolOptionType?: 'full' | 'short'; // 툴바 옵션 타입 ( full : 전체 툴바 도구 사용, short: 최소 툴바 도구 사용 )
}

const Root = styled.div<{ animation?: string }>`
  label {
    display: none !important;
  }

  .ck.ck-editor__top .ck-sticky-panel {
    .ck-sticky-panel__content {
      border: 1px solid var(--outline);
    }
  }

  .editor-container__editor {
    min-height: 23rem;
  }
`;

const TextEditor: React.FC<TextEditorProps> = ({ content, setContent, readOnly, disabled, idx, toolOptionType = 'full' }) => {
  const editorRef = useRef(null);

  // readOnly 상태에 따라 에디터 설정하는 함수
  const updateReadOnlyState = (editor: any) => {
    const toolbarElement = editor.ui.view.toolbar.element;
    if (readOnly) {
      editor.enableReadOnlyMode(editor.id);
      toolbarElement.style.display = 'none';
    } else {
      editor.disableReadOnlyMode(editor.id);
      toolbarElement.style.display = 'flex';
    }
  };

  // readOnly prop이 변경될 때마다 에디터 상태 업데이트
  useEffect(() => {
    if (editorRef.current) {
      updateReadOnlyState(editorRef.current);
    }
  }, [readOnly]);

  const { editorConfig } = useMemo(() => {
    return { editorConfig: toolOptionType === 'full' ? ckeditorConfig : shortCkeditorConfig };
  }, [toolOptionType]);

  return (
    <Root className={`ckeditor ${idx ? (idx === 0 ? 'ck_slide_up' : '') : 'ck_slide_up'}`}>
      <div className='editor-container__editor'>
        {editorConfig && (
          <CKEditor
            data={content}
            editor={ClassicEditor}
            config={editorConfig as any}
            onReady={(editor: any) => {
              editorRef.current = editor;
              updateReadOnlyState(editor); // 초기 readOnly 상태 설정

              // Base64 업로드 어댑터 설정 (이미지 압축 포함)
              editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
                return {
                  upload: () => {
                    return new Promise((resolve, reject) => {
                      loader.file.then(async (file: File) => {
                        try {
                          // 파일 크기 제한 (5MB)
                          if (file.size > 5 * 1024 * 1024) {
                            reject(new Error('파일 크기는 5MB를 초과할 수 없습니다.'));
                            return;
                          }

                          // 이미지 파일만 허용
                          if (!file.type.startsWith('image/')) {
                            reject(new Error('이미지 파일만 업로드할 수 있습니다.'));
                            return;
                          }

                          // SVG 파일은 압축하지 않음
                          const isSvg = file.type === 'image/svg+xml';
                          let processedFile: File | Blob = file;

                          if (!isSvg) {
                            // 이미지 압축 설정
                            const config = {
                              quality: 0.7,
                              maxWidth: 1280,
                              maxHeight: 720,
                              mimeType: 'image/jpeg' as const,
                            };

                            // 이미지 압축
                            processedFile = await readAndCompressImage(file, config);
                          }

                          // FileReader를 사용하여 Base64로 변환
                          const reader = new FileReader();
                          reader.onload = () => {
                            const base64String = reader.result as string;
                            resolve({
                              default: base64String,
                            });
                          };
                          reader.onerror = (error) => {
                            reject(error);
                          };
                          reader.readAsDataURL(processedFile);
                        } catch (error) {
                          reject(error);
                        }
                      });
                    });
                  },
                  abort: () => {
                    // 업로드 취소 처리
                  },
                };
              };
            }}
            onChange={(event: any, editor: any) => {
              const data = editor.getData();
              if (setContent && data !== content) {
                setContent(data);
              }
            }}
            onBlur={(editor: any) => {}}
            onFocus={(editor: any) => {}}
            disabled={disabled}
          />
        )}
      </div>
    </Root>
  );
};

export default TextEditor;
